import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import getConfig from 'next/config';
import { debounce } from 'lodash';

import {
  AutoCompleteAddress,
  AutoCompleteAddresses,
  DataSuggestions,
} from '@/core/interfaces/common';
import { InputWrapper } from '@/core/components/InputWrapper';

import { request } from '@/utils/request';
import {
  findLongestCommonSubstring,
  getFormattedAddress,
} from '@/utils/helpers';

import {
  AddressInputWrapper,
  AddressSuggestion,
  AddressSuggestions,
} from './AutoAddressInput.styles';

interface FormInputProps {
  backgroundcolor: string;
  error: () => string | false;
  isDisabled?: boolean;
  isRequired?: boolean;
  onBlur?: (streetAddress?: string, zipCode?: string) => void;
  onChangeAddress: (event: string) => void;
  onFocus: () => void;
  setZipCode: (event: string) => void;
  value: string;
}

const { REACT_APP_SMARTY_STREETS_API_KEY } = getConfig().publicRuntimeConfig;

export const AutoAddressInput = ({
  backgroundcolor,
  error,
  isDisabled,
  isRequired,
  onBlur,
  onChangeAddress,
  onFocus,
  setZipCode,
  value,
}: FormInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [
    addressSuggestions,
    setAddressSuggestions,
  ] = useState<AutoCompleteAddresses>([]);
  const [
    isAutocompletePending,
    setIsAutocompletePending,
  ] = useState(false);
  const [
    showSuggestions,
    setShowSuggestions,
  ] = useState(false);

  const handleFetchAddressSuggestions = async (
    inputValue: string, params: { selected?: string }
  ) => {
    setIsAutocompletePending(true);

    const addressSuggestion = !inputValue ?
      [] :
      await request
        .get<DataSuggestions>(
          `https://us-autocomplete-pro.api.smartystreets.com/lookup?key=${REACT_APP_SMARTY_STREETS_API_KEY}`,
          {
            params: {
              search: inputValue,
              ...params,
            },
          }
        )
        .then(({ data }) => data?.suggestions)
        .catch(({ response: { data = {} } = {} }) => {
          throw data;
        });

    setAddressSuggestions(addressSuggestion || []);
    setIsAutocompletePending(false);
    setShowSuggestions(true);
  };

  const debouncedFetchAddressSuggestions = useCallback(
    debounce((newInputValue: string) => {
      handleFetchAddressSuggestions(newInputValue, {});
    }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handlePickAddressSuggestion = (suggestion: AutoCompleteAddress) => {
    if (suggestion.entries > 1) {
      const selected = `${suggestion.streetLine} ${suggestion.secondary} (${suggestion.entries}) ${suggestion.city} ${suggestion.state} ${suggestion.zipcode}`;

      handleFetchAddressSuggestions(value, { selected });
    } else {
      setShowSuggestions(false);
    }

    setZipCode(suggestion.zipcode);
    onChangeAddress(`${suggestion.streetLine} ${suggestion.secondary}`);

    if (onBlur) {
      onBlur(`${suggestion.streetLine} ${suggestion.secondary}`, suggestion.zipcode);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Element)) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  // Sync input value with prop value
  useEffect(() => {
    const input = inputRef.current;

    if (input && input.value !== value) {
      input.value = value;
    }
  }, [value]);

  // Detect browser autofill - browser autofill does NOT otherwise
  // trigger the target lookup. This is required for message mapped
  // action centers.
  useEffect(() => {
    const input = inputRef.current;

    if (!input) return undefined;

    const checkForAutofill = (): void => {
      const currentValue = input.value;

      // Check if zip code field also got autofilled
      const zipCodeInput = document.querySelector('input[name="zipCode"]') as HTMLInputElement;
      const autofilledZipCode = zipCodeInput?.value || '';

      if (currentValue && currentValue !== value) {
        onChangeAddress(currentValue);

        // If zip code was autofilled, update it via setZipCode
        if (autofilledZipCode) {
          setZipCode(autofilledZipCode);
        }

        if (onBlur) {
          onBlur(currentValue, autofilledZipCode);
        }
      }
    };

    // Animation event for webkit autofill detection
    const handleAnimationStart = (e: AnimationEvent): void => {
      if (e.animationName === 'onAutoFillStart') {
        checkForAutofill();
      }
    };

    // Focus/click events - iOS often requires interaction after autofill
    const handleFocusOrClick = (): void => {
      setTimeout(checkForAutofill, 0);
    };

    // Change/input events - most reliable for iOS and modern browsers
    const handleChangeOrInput = (): void => {
      checkForAutofill();
    };

    input.addEventListener('animationstart', handleAnimationStart as EventListener);
    input.addEventListener('focus', handleFocusOrClick);
    input.addEventListener('click', handleFocusOrClick);
    input.addEventListener('change', handleChangeOrInput);
    input.addEventListener('input', handleChangeOrInput);

    return () => {
      input.removeEventListener('animationstart', handleAnimationStart as EventListener);
      input.removeEventListener('focus', handleFocusOrClick);
      input.removeEventListener('click', handleFocusOrClick);
      input.removeEventListener('change', handleChangeOrInput);
      input.removeEventListener('input', handleChangeOrInput);
    };
  },
  [
    value,
    onChangeAddress,
    onBlur,
    setZipCode,
  ]);

  const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeAddress(event.target.value);
    debouncedFetchAddressSuggestions(event.target.value);
  };

  const renderSuggestion = (suggestion: AutoCompleteAddress) => {
    const wholeAddress = getFormattedAddress(suggestion);
    const [
      commonPart,
      restOfTheString,
    ] = findLongestCommonSubstring(value, wholeAddress);

    return (
      <>
        {commonPart}
        <strong>
          {restOfTheString}
        </strong>
      </>
    );
  };

  return (
    <AddressInputWrapper ref={wrapperRef}>
      <InputWrapper
        backgroundcolor={backgroundcolor}
        error={error}
        isRequired={isRequired}
      >
        <input
          // We want autocomplete off for the address to ensure the
          // address validation is used, but 'new-value' is an invalid
          // value and at least in a11y tests this is flagged as an issue.
          // Of note: browsers can still auto-fill (which is good).
          autoComplete="off"
          disabled={isDisabled}
          type="text"
          name="streetAddress"
          placeholder=" "
          onChange={handleChangeValue}
          onFocus={onFocus}
          required={isRequired}
          defaultValue={value}
          ref={inputRef}
        />
        <span>Address</span>
      </InputWrapper>
      <AddressSuggestions showSuggestions={showSuggestions}>
        {isAutocompletePending &&
          !addressSuggestions.length && <AddressSuggestion>...Loading</AddressSuggestion>
        }
        {addressSuggestions.map(addressSuggestion => (
          <AddressSuggestion
            key={`${addressSuggestion.streetLine}${addressSuggestion.entries}${addressSuggestion.secondary}`}
            onClick={() => handlePickAddressSuggestion(addressSuggestion)}
            data-testid="address-suggestion"
          >
            {renderSuggestion(addressSuggestion)}
          </AddressSuggestion>
        ))}
      </AddressSuggestions>
    </AddressInputWrapper>
  );
};

AutoAddressInput.defaultProps = {
  isDisabled: false,
  isRequired: false,
  onBlur: () => null,
};
